import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  Fade,
  useMediaQuery,
  useTheme,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Recipe, RecipeStep } from '../models/Recipe';
import { RecipeChange, AgentProgressEvent } from '../types/agentTypes';
import recipeService from '../services/recipeService';
import { pruneChatHistory } from '../utils/tokenUtils';

export type ChatMessageType = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: number;
};

interface AIChatDialogProps {
  open: boolean;
  onClose: () => void;
  context: { recipe: Recipe; stepIndex?: number };
  onApplyRevisions?: (modifiedRecipe: Recipe) => Promise<void>;
  initialMessage?: string;
}

const SUGGESTIONS = [
  'It looks different than expected',
  'I don\'t have this ingredient',
  'It\'s too salty/bland - how do I fix it?',
  'How do I know when it\'s done?',
  'I made a mistake - help!',
  'Can I skip or modify this step?',
];

function getChatStorageKey(recipeId: string, stepIndex?: number) {
  return stepIndex !== undefined
    ? `chat:recipe:${recipeId}:step:${stepIndex}`
    : `chat:recipe:${recipeId}:all`;
}

const AIChatDialog: React.FC<AIChatDialogProps> = ({
  open,
  onClose,
  context,
  onApplyRevisions,
  initialMessage,
}) => {
  const { recipe, stepIndex } = context;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [includeContext, setIncludeContext] = useState(true);
  const [revisedSteps, setRevisedSteps] = useState<RecipeStep[] | null>(null);
  const [assistantText, setAssistantText] = useState<string | null>(null);
  const [pendingRecipe, setPendingRecipe] = useState<Recipe | null>(null);
  const [changesSummary, setChangesSummary] = useState<RecipeChange[]>([]);
  const [showChangesDetails, setShowChangesDetails] = useState(false);
  const [progress, setProgress] = useState<AgentProgressEvent | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Load chat history from localStorage
  useEffect(() => {
    if (open) {
      const key = getChatStorageKey(recipe.id, stepIndex);
      const raw = localStorage.getItem(key);
      if (raw) {
        try {
          setMessages(JSON.parse(raw));
        } catch {
          setMessages([]);
        }
      } else {
        setMessages([]);
      }
      setInput('');
      setError(null);
      setRevisedSteps(null);
      setAssistantText(null);
      setPendingRecipe(null);
      setChangesSummary([]);
      setShowChangesDetails(false);
    }
  }, [open, recipe.id, stepIndex]);

  // Save chat history to localStorage (with pruning)
  useEffect(() => {
    if (open && messages.length > 0) {
      const key = getChatStorageKey(recipe.id, stepIndex);

      // Prune messages to prevent localStorage bloat
      const prunedMessages = pruneChatHistory(
        messages.map(m => ({ role: m.role, content: m.content }))
      );

      // Convert back to ChatMessageType format
      const messagesToSave = prunedMessages.map((m, idx) => {
        const originalIndex = messages.length - prunedMessages.length + idx;
        const original = messages[originalIndex] || messages[messages.length - 1];
        return {
          id: original?.id || `${Date.now()}-${idx}`,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          createdAt: original?.createdAt || Date.now(),
        };
      });

      localStorage.setItem(key, JSON.stringify(messagesToSave));
    }
  }, [messages, open, recipe.id, stepIndex]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, assistantText, revisedSteps]);

  // Handle initial message from proactive prompts
  useEffect(() => {
    if (open && initialMessage && initialMessage.trim()) {
      setInput(initialMessage);
      setShowSuggestions(false);
    }
  }, [open, initialMessage]);

  const handleSend = async (text: string) => {
    if (!text.trim()) {
      // Prevent sending empty messages
      return;
    }
    setSending(true);
    setProgress(null);
    setError(null);

    const userMsg: ChatMessageType = {
      id: `${Date.now()}-user`,
      role: 'user',
      content: text.trim(),
      createdAt: Date.now(),
    };

    const history = [...messages, userMsg];

    try {
      const aiResult = await recipeService.sendRecipeChatMessage({
        recipe,
        stepIndex,
        messages: history.map(m => ({
          role: m.role,
          content: m.content,
        })),
        userMessage: text.trim(),
        mode: 'general',
        onProgress: (event) => setProgress(event),
      });

      const assistantMsg: ChatMessageType = {
        id: `${Date.now()}-assistant`,
        role: 'assistant',
        content: aiResult.assistantText,
        createdAt: Date.now(),
      };

      setMessages([...history, assistantMsg]);
      setAssistantText(aiResult.assistantText || null);
      setRevisedSteps(aiResult.revisedSteps || null);
      setPendingRecipe(aiResult.modifiedRecipe || null);
      setChangesSummary(aiResult.changes || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to get response');
    } finally {
      setSending(false);
      setProgress(null);
      setInput('');
    }
  };

  const handleSuggestion = (suggestion: string) => {
    setInput(suggestion);
    setShowSuggestions(false);
  };

  const handleApplyRevisions = async () => {
    if (pendingRecipe && onApplyRevisions) {
      await onApplyRevisions(pendingRecipe);
      setPendingRecipe(null);
      setChangesSummary([]);
      setRevisedSteps(null);
    }
  };

  const handleRejectChanges = () => {
    setPendingRecipe(null);
    setChangesSummary([]);
    setRevisedSteps(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      sx={{
        zIndex: 1400,
        '& .MuiDialog-container': {
          alignItems: isMobile ? 'flex-start' : 'center',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Chat with Otto
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: { xs: '100%', sm: 400 }, display: 'flex', flexDirection: 'column', p: 0 }}>
        <Box sx={{ px: 3, pt: 2, pb: 1, flex: 1, overflowY: 'auto' }} ref={scrollRef}>
          {messages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ask Otto anything about this step or recipe. You can ask follow-up questions, and Otto will remember the context.
            </Typography>
          )}
          {messages.map((msg) => (
            <Fade in key={msg.id}>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-end',
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    bgcolor: msg.role === 'user' ? 'primary.light' : 'grey.100',
                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    borderRadius: 2,
                    p: 2,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {msg.content}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6, mt: 0.5 }}>
                    {msg.role === 'user' ? 'You' : 'Otto'}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          ))}
          {/* Show pending changes card */}
          {pendingRecipe && changesSummary.length > 0 && (
            <Fade in>
              <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', border: 2, borderColor: 'warning.main', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom color="warning.dark">
                  📝 Pending Changes
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Otto suggests the following modifications to your recipe:
                </Alert>
                <List dense>
                  {changesSummary.map((change, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={change.description}
                        secondary={change.details}
                        primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.95rem' }}
                        secondaryTypographyProps={{ fontSize: '0.85rem' }}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* Toggle for before/after comparison */}
                <Button
                  size="small"
                  onClick={() => setShowChangesDetails(!showChangesDetails)}
                  endIcon={showChangesDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ mt: 1, mb: 1 }}
                >
                  {showChangesDetails ? 'Hide Details' : 'Show Details'}
                </Button>

                <Collapse in={showChangesDetails}>
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Before & After Comparison:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Current Recipe:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.85rem', mt: 1 }}>
                          {recipe.ingredients}
                        </Typography>
                        {recipe.steps?.map((step, idx) => (
                          <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                            {idx + 1}. {step.content}
                          </Typography>
                        ))}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="primary">
                          <strong>Modified Recipe:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.85rem', mt: 1 }}>
                          {pendingRecipe.ingredients}
                        </Typography>
                        {pendingRecipe.steps?.map((step, idx) => (
                          <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                            {idx + 1}. {step.content}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Collapse>

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApplyRevisions}
                    fullWidth
                  >
                    Apply Changes
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleRejectChanges}
                    fullWidth
                  >
                    Reject
                  </Button>
                </Box>
              </Box>
            </Fade>
          )}
        </Box>
        {error && (
          <Box sx={{ px: 3, py: 1 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        )}
        <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              variant="outlined"
              placeholder="Type your question..."
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={sending}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (input.trim()) { handleSend(input); }
                }
              }}
              sx={{ mr: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              disabled={sending || !input.trim()}
              onClick={() => handleSend(input)}
              sx={{ minWidth: 100 }}
            >
              {sending ? <CircularProgress size={20} /> : 'Send'}
            </Button>
          </Box>
          {showSuggestions && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {SUGGESTIONS.map((suggestion, idx) => (
                <Chip
                  key={idx}
                  label={suggestion}
                  onClick={() => handleSuggestion(suggestion)}
                  clickable
                  color="secondary"
                  sx={{ fontSize: '0.95rem' }}
                />
              ))}
            </Box>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={includeContext}
                onChange={e => setIncludeContext(e.target.checked)}
                color="primary"
              />
            }
            label="Include full recipe context"
            sx={{ mt: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* OttoThinking inside Dialog to ensure visibility on mobile */}
      {sending && progress && (
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 16, md: 80 },
            left: { xs: 16, md: 24 },
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              boxShadow: 3,
              minWidth: { xs: 'auto', md: 200 },
            }}
          >
            <img
              src={`/assets/images/otto/otto_think_${Math.floor(Date.now() / 500) % 4 + 1}.svg`}
              alt="Otto is thinking..."
              width={60}
              height={60}
            />
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" fontWeight="bold">
                {progress.phase === 'calling_tool' || progress.phase === 'tool_complete'
                  ? progress.toolName ? `Working on ${progress.toolName}...` : 'Thinking...'
                  : 'Thinking...'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {progress.phase === 'done' ? '✓ Complete!' : `Step ${progress.iteration}`}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      </Dialog>
    </>
  );
};

// Memoize to prevent unnecessary re-renders when parent state changes
export default React.memo(AIChatDialog);
